import { Component, type ReactNode } from "react";

/**
 * Map technical error messages to user-friendly ones
 */
const ERROR_MESSAGE_MAP: Record<string, string> = {
  // Network errors
  "network error": "Unable to connect. Please check your internet connection.",
  "failed to fetch": "Unable to connect to the server. Please try again.",
  timeout: "The request took too long. Please try again.",
  ERR_NETWORK: "Network connection lost. Please check your internet.",

  // Auth errors
  "401": "Your session has expired. Please log in again.",
  unauthorized: "You need to log in to access this.",
  "403": "You don't have permission to do this.",
  forbidden: "Access denied. Contact your administrator if you need access.",

  // Validation errors
  "400": "Something wasn't quite right. Please check your input.",
  "bad request": "Please check the information you entered.",
  "validation error": "Some fields need to be corrected.",

  // Server errors
  "500": "Something went wrong on our end. We're working on it.",
  "internal server error": "An unexpected error occurred. Please try again.",
  "502": "The server is temporarily unavailable. Please try again shortly.",
  "503":
    "The service is temporarily unavailable. Please try again in a few minutes.",
  "504": "The server is taking too long to respond. Please try again.",

  // Common errors
  "not found": "The item you're looking for couldn't be found.",
  "404": "This page or item doesn't exist.",
  conflict: "This conflicts with existing data. Please refresh and try again.",
  "409": "This action conflicts with another change. Please refresh the page.",

  // Database errors (sanitized)
  "unique constraint": "This already exists. Please use a different value.",
  duplicate: "This item already exists.",
  "foreign key": "This item is linked to other data and cannot be changed.",

  // File errors
  "file too large": "The file is too large. Please use a smaller file.",
  "invalid file type": "This file type isn't supported.",
  "upload failed": "Failed to upload the file. Please try again.",
};

/**
 * Convert technical error to user-friendly message
 */
export function humanizeError(error: unknown): string {
  if (!error) return "An unexpected error occurred.";

  // Extract message from various error shapes
  let message = "";
  if (typeof error === "string") {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;
    message = String(err.message || err.error || err.statusText || "");
  }

  const lowerMessage = message.toLowerCase();

  // Check for matches in our map
  for (const [key, friendly] of Object.entries(ERROR_MESSAGE_MAP)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return friendly;
    }
  }

  // If message looks technical (contains code-like patterns), return generic
  if (/[<>{}[\]\\\/]|error:|exception|stack|at\s+\w+/i.test(message)) {
    return "Something went wrong. Please try again or contact support.";
  }

  // If message is reasonably short and doesn't look like code, use it
  if (message.length > 0 && message.length < 200) {
    // Capitalize first letter
    return message.charAt(0).toUpperCase() + message.slice(1);
  }

  return "An unexpected error occurred. Please try again.";
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - Catches React errors and displays user-friendly message
 * Prevents entire app from crashing on component errors
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging (in production, send to error tracking service)
    console.error("ErrorBoundary caught: ", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center px-6 py-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-500">
            <svg
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-slate-900">
            Something went wrong
          </h2>

          <p className="mt-2 max-w-md text-sm text-slate-600">
            {humanizeError(this.state.error)}
          </p>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-brand px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:shadow-md"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>

            <button
              onClick={this.handleRefresh}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Refresh Page
            </button>
          </div>

          <p className="mt-6 text-xs text-slate-400">
            If this keeps happening, please contact support.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorMessage - Inline error display for form fields and API errors
 */
export function ErrorMessage({
  error,
  className = "",
}: {
  error: unknown;
  className?: string;
}) {
  if (!error) return null;

  const message = humanizeError(error);

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 ${className}`}
      role="alert"
    >
      <svg
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{message}</span>
    </div>
  );
}

export default ErrorBoundary;

/**
 * ErrorToast - Toast notification for transient errors
 */
export function ErrorToast({
  error,
  onDismiss,
}: {
  error: unknown;
  onDismiss?: () => void;
}) {
  if (!error) return null;

  const message = humanizeError(error);

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex max-w-sm items-start gap-3 rounded-lg border border-rose-200 bg-white p-4 shadow-lg"
      role="alert"
    >
      <div className="flex-shrink-0 rounded-full bg-rose-100 p-1.5 text-rose-600">
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <div className="flex-1 pt-0.5">
        <p className="text-sm font-medium text-slate-900">Error</p>
        <p className="mt-1 text-sm text-slate-600">{message}</p>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
